/**
 * CSS — Contrats Salariés Sociétés
 */
import Observer from '~/utils/lib/core/observer'
import { ifElse, includes, propEq } from '~/utils/lib/core/prelude'

/**
 * @constant {string}
 * @default
 */
export const STORE_PREFIX = 'css'

/** @constant {string} CSS (Contrats Salariés Sociétés) API base URL */
export const baseURL = import.meta.env.VITE_CSS_API_URL

/**
 * @typedef {object} RequestConstructorParams
 * @property {string} options.method
 * @property {string} options.route
 * @property {object} [options.data]
 * @property {object} [options.params] will hydrate URL params
 */

/**
 * @typedef {import('ofetch').FetchResponse<any>} RequestResponse
 * @desc Ofetch response corresponds to standard Response object
 */

/**
 *
 * @typedef {import('ofetch').CreateFetchOptions["defaults"] & {
 *    withAuthorization: boolean;
 *    execCount: number;
 *    url: string;
 * }} RequestConfig
 * @desc Extending Ofetch default config with extra properties
 */

/**
 * @typedef {Pick<import('ofetch').FetchError, 'statusCode' | 'statusMessage' | 'data' | 'request' | 'response'>} RequestError
 * @desc Using ofetch FetchError type to only take used and useful properties
 */

/** @type {Observer} */
export const observer = new Observer()

/** @constant {object} */
export const ERRORS = {
  REQUEST_RESPONSE_UNKNOWN_SCHEMA: '<RequestError> with unknown schema',
  REQUEST_RESPONSE_UNKNOWN_DATA: '<RequestError.data> with unknown schema',
  REQUEST_RESPONSE_UNKNOWN_MESSAGE: '<RequestError.data> with unknown "message"',
  REQUEST_RESPONSE_UNKNOWN_CAUSE: '<RequestError.data> with unknown "cause"',
}

/**
 * @enum {typeof MICRO_SERVICE_ERRORS[keyof typeof MICRO_SERVICE_ERRORS]}
 * @readonly
 */
export const MICRO_SERVICE_ERRORS = /** @type {const} */ ({
  MISC: 'miscellaneous microservice error returned',
})

/**
 * @typedef {RequestResponseReflection_Right | RequestResponseReflection_Left} RequestResponseReflection
 * @desc
 *    Micro service success request response reflection,
 *    "contextual response" pattern (including a discriminating property
 *    `status` in an "either~like" pattern)
 *
 * @typedef {Object} RequestResponseReflection_Right
 * @property {REQUEST_RESPONSE_STATUS["SUCCESS"]} status
 * @property {RequestResponse["_data"]} data
 *
 * @typedef {Object} RequestResponseReflection_Left
 * @property {REQUEST_RESPONSE_STATUS["ERROR"]} status
 * @property {RequestResponse["_data"]} error
 */

/**
 * @typedef {Object} REQUEST_RESPONSE_STATUS
 * @property {string} [SUCCESS="success"]
 * @property {string} [ERROR="error"]
 */
export const REQUEST_RESPONSE_STATUS = {
  SUCCESS: 'success',
  ERROR: 'error',
}

/**
 * @desc Reverse reflection of the `RequestResponseReflection` into a Promise
 * @type {(_: RequestResponseReflection) => Promise<
 *  RequestResponseReflection_Right["data"]
 * >} or rejects `RequestResponseReflection_Left["error"]`
 */
// @ts-expect-error: TypeScript currying limitation
export const requestResponseReflectionToPromise = ifElse(
  (/** @type {RequestResponseReflection} */ res) => propEq('status', REQUEST_RESPONSE_STATUS.SUCCESS, res),
  (/** @type {RequestResponseReflection_Right} */ { data }) => Promise.resolve( data ),
  (/** @type {RequestResponseReflection_Left} */ { error }) => Promise.reject( error )
)
/**
 * @desc Predicate discriminating a union
 * @param {RequestResponse["_data"]} results
 * @type {(_: any | RequestResponseReflection) => boolean}
 * @returns {boolean}
 */
export const responseResponseReflectionPredicate = results => {
  return includes(
    results.status || '',
    [
      REQUEST_RESPONSE_STATUS.SUCCESS,
      REQUEST_RESPONSE_STATUS.ERROR
    ]
  )
}
